import api from './api';
import { Product } from '../types';
import { registerBoxDiscount } from '../data/boxDiscount';

export interface ProductListParams {
  category?: string;
  brand?: string;
  q?: string;
  /** Se true, ``q`` filtra solo su codici prodotto (SKU/DTK/OEM), non su titolo/descrizione. */
  codeOnly?: boolean;
  /** Se true (solo dropdown ricerca navbar), ripete un prodotto in righe distinte per ogni codice OEM che matcha ``q``. Default false = un prodotto = una riga (catalogo). */
  expandOemMatches?: boolean;
  type?: string;
  color?: string;
  skus?: string;
  /** Nasconde i prodotti non visibili sul web e/o senza prezzo di listino. */
  publicOnly?: boolean;
  /** Filtri admin tri-state: true/false filtra, undefined ignora */
  isKit?: boolean;
  hasBoxDiscount?: boolean;
  hasPrice?: boolean;
  hasStock?: boolean;
  /** Filtra solo articoli con flag NTS ``ar_inesaur='S'`` (pagina Outlet). */
  inEsaurimento?: boolean;
  page?: number;
  limit?: number;
}

export interface ProductListResponse {
  items: Product[];
  total: number;
  page: number;
  limit: number;
  pages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

function normalizeProduct(raw: any): Product {
  // Backend stores "yield" as a field name; map yield_ alias back to yield
  if (raw.yield_ !== undefined && raw.yield === undefined) {
    raw.yield = raw.yield_;
    delete raw.yield_;
  }
  // Idrata il registry sconto scatola (sorgente: backend MongoDB).
  if (raw.sku) {
    registerBoxDiscount(raw.sku, raw.boxQty, raw.boxPrice, raw.boxDemo);
  }
  // Lo stock non può essere negativo in vetrina: NTS può restituire valori
  // negativi (es. ordini aperti > giacenza fisica). Cappiamo a 0.
  if (typeof raw.stock === 'number' && raw.stock < 0) {
    raw.stock = 0;
  }
  return raw as Product;
}

export const productService = {
  async list(params: ProductListParams = {}): Promise<ProductListResponse> {
    const query = new URLSearchParams();
    if (params.category) query.set('category', params.category);
    if (params.brand) query.set('brand', params.brand);
    if (params.q) query.set('q', params.q);
    if (params.codeOnly) query.set('codeOnly', 'true');
    if (params.expandOemMatches) query.set('expandOemMatches', 'true');
    if (params.type) query.set('type', params.type);
    if (params.color) query.set('color', params.color);
    if (params.skus) query.set('skus', params.skus);
    if (params.publicOnly) query.set('publicOnly', 'true');
    if (params.isKit !== undefined) query.set('isKit', String(params.isKit));
    if (params.hasBoxDiscount !== undefined) query.set('hasBoxDiscount', String(params.hasBoxDiscount));
    if (params.hasPrice !== undefined) query.set('hasPrice', String(params.hasPrice));
    if (params.hasStock !== undefined) query.set('hasStock', String(params.hasStock));
    if (params.inEsaurimento !== undefined) query.set('inEsaurimento', String(params.inEsaurimento));
    if (params.page) query.set('page', String(params.page));
    if (params.limit) query.set('limit', String(params.limit));

    const { data } = await api.get<ProductListResponse>(`/api/products?${query}`);
    return { ...data, items: data.items.map(normalizeProduct) };
  },

  async get(sku: string, opts: { publicOnly?: boolean } = {}): Promise<Product> {
    const query = new URLSearchParams();
    if (opts.publicOnly) query.set('publicOnly', 'true');
    const qs = query.toString();
    const { data } = await api.get<Product>(
      `/api/products/${encodeURIComponent(sku)}${qs ? `?${qs}` : ''}`,
    );
    return normalizeProduct(data);
  },

  /** Prodotti in evidenza: promo attive.
   * @param onlyTempo - se true, esclude le promo permanenti (homepage/offerte page)
   */
  async featured(limit = 12, onlyTempo = false): Promise<ProductListResponse> {
    const url = `/api/products/featured?limit=${limit}${onlyTempo ? '&only_tempo=true' : ''}`;
    const { data } = await api.get<ProductListResponse>(url);
    return { ...data, items: data.items.map(normalizeProduct) };
  },

  /** Modelli macchina compatibili con uno SKU — fonte: abbinatore_associazioni. */
  async compatibleModels(sku: string): Promise<Array<{ id: string; nome: string; marca: string; tipoMacchinaLabel: string; codiceOem: string }>> {
    const { data } = await api.get<{ modelli: Array<{ id: string; nome: string; marca: string; tipoMacchina: string; tipoMacchinaLabel: string; codiceOem: string }> }>(
      `/api/abbinatore/sku/${encodeURIComponent(sku)}/modelli`,
    );
    return data.modelli;
  },
};
